import { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Loader2, QrCode, ExternalLink, ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AdminWeddingQR
 *
 * A minimal, self-contained tool that lets Mary & Arrington download the
 * wedding website's homepage QR code as either an SVG (infinitely scalable)
 * or a high-res PNG (print-ready). Intentionally scoped narrow — the future
 * Smart QR Studio will live elsewhere.
 */
const AdminWeddingQR = ({ authHeader }) => {
  const [info, setInfo] = useState({ url: '' });
  const [previewSrc, setPreviewSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(''); // 'svg' | 'png-2048' | 'png-4096' | 'pdf-5x7' | 'pdf-4x6'
  const [monogram, setMonogram] = useState(false);
  // Server-side scan verification — 'checking' | 'verified' | 'mismatch' | 'unreadable' | 'error'
  const [scanStatus, setScanStatus] = useState('checking');
  const [scanDetail, setScanDetail] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [infoRes, svgRes] = await Promise.all([
          axios.get(`${API}/admin/qr/info`, { headers: authHeader() }),
          axios.get(`${API}/admin/qr/svg`, {
            headers: authHeader(),
            responseType: 'blob',
            params: monogram ? { monogram: 1 } : {},
          }),
        ]);
        if (!alive) return;
        setInfo(infoRes.data || { url: '' });
        setPreviewSrc((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(new Blob([svgRes.data], { type: 'image/svg+xml' }));
        });
      } catch (err) {
        console.error('QR load failed', err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [authHeader, monogram]);

  // Run the server-side scan test whenever the monogram toggle changes.
  // Decodes a freshly-rendered 512 px PNG using zbar and checks the string
  // matches the destination URL — so a pass truly means "prints & scans".
  useEffect(() => {
    let alive = true;
    setScanStatus('checking');
    setScanDetail(null);
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/qr/verify`, {
          headers: authHeader(),
          params: monogram ? { monogram: 1 } : {},
        });
        if (!alive) return;
        const d = res.data || {};
        setScanDetail(d);
        if (d.ok) setScanStatus('verified');
        else if (!d.decoded) setScanStatus('unreadable');
        else setScanStatus('mismatch');
      } catch (err) {
        if (!alive) return;
        setScanStatus('error');
      }
    })();
    return () => { alive = false; };
  }, [authHeader, monogram]);

  const download = async (kind) => {
    setDownloading(kind);
    try {
      const isSvg = kind === 'svg';
      const size = kind === 'png-4096' ? 4096 : 2048;
      const url = isSvg ? `${API}/admin/qr/svg` : `${API}/admin/qr/png`;
      // Cache-bust every request so a stale CDN/browser copy can never be
      // returned instead of the freshly-rendered file with the monogram.
      const params = { _: Date.now() };
      if (!isSvg) params.size = size;
      if (monogram) params.monogram = 1;
      const res = await axios.get(url, {
        headers: authHeader(),
        responseType: 'blob',
        params,
      });
      const mime = isSvg ? 'image/svg+xml' : 'image/png';
      const monoTag = monogram ? '-monogram' : '';
      const ext = isSvg ? `${monoTag}.svg` : `${monoTag}-${size}px.png`;
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: mime }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `mary-arrington-wedding-qr${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      console.error('QR download failed', err);
      alert('Could not download the QR code. Please try again.');
    } finally {
      setDownloading('');
    }
  };

  const downloadPdf = async (size /* '5x7' | '4x6' */) => {
    setDownloading(`pdf-${size}`);
    try {
      const res = await axios.get(`${API}/admin/qr-sheet.pdf`, {
        headers: authHeader(),
        responseType: 'blob',
        params: { size, monogram: monogram ? 1 : 0, _: Date.now() },
      });
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `mary-arrington-qr-card-${size}in.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
    } catch (err) {
      console.error('QR card PDF download failed', err);
      alert('Could not download the print card. Please try again.');
    } finally {
      setDownloading('');
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8" data-testid="admin-wedding-qr">
      <div className="flex items-start gap-3 mb-2">
        <QrCode className="w-6 h-6 text-[#C8A951] mt-1" />
        <div>
          <h2 className="font-serif text-2xl text-[#3B2F2F]">Wedding Website QR Code</h2>
          <p className="text-[#5A4A4A] text-sm mt-1 max-w-xl">
            A scannable link straight to the wedding homepage — perfect for printed invitations,
            welcome signs, and the ceremony program. Guests always land on the front door of the
            site first, so they can experience Mary &amp; Arrington&apos;s story before hitting RSVP.
          </p>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-[220px_1fr] gap-6 md:gap-8 items-start">
        {/* Preview column — full-size preview + scan-test at ~1" */}
        <div className="flex flex-col items-center gap-4">
          <div className="rounded-xl border border-[#E5E0D8] bg-[#F8F5EF] p-4 flex items-center justify-center w-full">
            {loading ? (
              <div className="w-[188px] h-[188px] flex items-center justify-center text-[#8A7A7A]">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : previewSrc ? (
              <img
                src={previewSrc}
                alt="Wedding website QR code"
                className="w-[188px] h-[188px] block"
                data-testid="wedding-qr-preview"
              />
            ) : (
              <div className="w-[188px] h-[188px] flex items-center justify-center text-red-500 text-sm">
                Could not load preview
              </div>
            )}
          </div>

          {/* Scan-test preview — approximately 1 inch (96px @ 96dpi).
              Visual only; the export dimensions are unchanged. Uses the same
              live SVG blob so URL / monogram / design changes update it too. */}
          <div className="w-full rounded-xl border border-dashed border-[#E5E0D8] bg-white p-3 flex flex-col items-center gap-2" data-testid="wedding-qr-scan-test-card">
            <p className="text-[10px] uppercase tracking-widest text-[#8A7A7A]">Scan test · ~1 inch</p>
            {previewSrc && !loading ? (
              <img
                src={previewSrc}
                alt="QR code at approximately 1 inch — scan-test preview"
                className="block"
                style={{ width: '96px', height: '96px' }}
                data-testid="wedding-qr-scan-test-preview"
              />
            ) : (
              <div className="w-[96px] h-[96px] bg-[#F8F5EF] rounded" />
            )}
            <p className="text-[10px] text-[#8A7A7A] text-center leading-tight max-w-[168px]">
              Try scanning this with your phone to confirm readability before printing.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div>
          <div className="mb-4">
            <p className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1">Destination</p>
            <a
              href={info.url || '#'}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[#3B2F2F] font-medium hover:text-[#C8A951] break-all"
              data-testid="wedding-qr-url"
            >
              {info.url || '—'}
              {info.url && <ExternalLink className="w-3.5 h-3.5" />}
            </a>
          </div>

          <label className="inline-flex items-center gap-2 mb-4 cursor-pointer select-none" data-testid="wedding-qr-monogram-label">
            <input
              type="checkbox"
              checked={monogram}
              onChange={(e) => setMonogram(e.target.checked)}
              className="w-4 h-4 accent-[#C8A951]"
              data-testid="wedding-qr-monogram-toggle"
            />
            <span className="text-sm text-[#3B2F2F]">
              Add <span className="font-serif italic">M&amp;A</span> monogram to the center
            </span>
          </label>

          {/* Live server-side scan test */}
          <ScanTestPill status={scanStatus} detail={scanDetail} />

          <div className="flex flex-col sm:flex-row flex-wrap gap-3">
            <button
              onClick={() => download('svg')}
              disabled={!!downloading}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#3B2F2F] text-white hover:bg-[#2c2222] transition-colors disabled:opacity-60"
              data-testid="wedding-qr-download-svg"
            >
              {downloading === 'svg'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Download className="w-4 h-4" />}
              Download SVG
              <span className="text-xs text-white/60 ml-1">(vector)</span>
            </button>

            <button
              onClick={() => download('png-2048')}
              disabled={!!downloading}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#C8A951] text-white hover:bg-[#B09240] transition-colors disabled:opacity-60"
              data-testid="wedding-qr-download-png"
            >
              {downloading === 'png-2048'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Download className="w-4 h-4" />}
              Download PNG
              <span className="text-xs text-white/70 ml-1">(2048×2048, ~7&quot; @ 300dpi)</span>
            </button>

            <button
              onClick={() => download('png-4096')}
              disabled={!!downloading}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-[#E5E0D8] text-[#3B2F2F] hover:bg-[#F8F5EF] transition-colors disabled:opacity-60"
              data-testid="wedding-qr-download-png-xl"
            >
              {downloading === 'png-4096'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Download className="w-4 h-4" />}
              Extra-large PNG
              <span className="text-xs text-[#8A7A7A] ml-1">(4096×4096)</span>
            </button>
          </div>

          {/* Print-ready invitation card PDF — vector-first, crop marks
              included, one click to hand to a printer. */}
          <div className="mt-5 p-4 rounded-xl bg-[#F8F5EF] border border-[#E5E0D8]">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div>
                <p className="font-serif text-[#3B2F2F] text-sm">Print-Ready Invitation Card</p>
                <p className="text-[11px] text-[#8A7A7A] mt-0.5">
                  Single card centered on US Letter, hairline crop marks, everything vector — hand it straight to a stationer.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap gap-2">
              <button
                onClick={() => downloadPdf('5x7')}
                disabled={!!downloading}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#3B2F2F] text-white hover:bg-[#2c2222] text-xs disabled:opacity-60"
                data-testid="wedding-qr-download-pdf-5x7"
              >
                {downloading === 'pdf-5x7'
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Download className="w-3.5 h-3.5" />}
                Download 5×7 in PDF
              </button>
              <button
                onClick={() => downloadPdf('4x6')}
                disabled={!!downloading}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full border border-[#3B2F2F] text-[#3B2F2F] hover:bg-white text-xs disabled:opacity-60"
                data-testid="wedding-qr-download-pdf-4x6"
              >
                {downloading === 'pdf-4x6'
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <Download className="w-3.5 h-3.5" />}
                Download 4×6 in PDF
              </button>
            </div>
          </div>

          <div className="mt-5 text-xs text-[#8A7A7A] leading-relaxed">
            <p><strong className="text-[#3B2F2F]">Printing tips:</strong> use the SVG whenever your designer supports it — it prints razor-sharp at any size. The 2048×2048 PNG is ideal for most invitations; use the extra-large PNG for large welcome signs or step-and-repeat backdrops.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminWeddingQR;

/**
 * ScanTestPill
 *
 * Compact "Verified scannable" indicator. Runs a real server-side decode of
 * the currently-configured QR (with or without the monogram) using zbar and
 * checks the decoded string equals the destination URL — so a green pill
 * means the printed QR will actually resolve when scanned.
 */
const ScanTestPill = ({ status, detail }) => {
  const cfg = {
    checking:   { bg: 'bg-[#F8F5EF]', text: 'text-[#8A7A7A]',  border: 'border-[#E5E0D8]', Icon: Loader2, label: 'Running scan test…', spin: true },
    verified:   { bg: 'bg-[#EFF6EC]', text: 'text-[#3E7A2E]',  border: 'border-[#B9D9AF]', Icon: ShieldCheck,    label: 'Verified scannable' },
    mismatch:   { bg: 'bg-[#FDF3E7]', text: 'text-[#8A5A20]',  border: 'border-[#E7C68B]', Icon: ShieldAlert,    label: 'Scans, but wrong URL' },
    unreadable: { bg: 'bg-[#FBEBEB]', text: 'text-[#8A2E2E]',  border: 'border-[#E7A8A8]', Icon: ShieldAlert,    label: 'Not scannable — please retry' },
    error:      { bg: 'bg-[#F8F5EF]', text: 'text-[#8A7A7A]',  border: 'border-[#E5E0D8]', Icon: ShieldQuestion, label: 'Scan test unavailable' },
  }[status] || {};
  const { Icon = ShieldQuestion, bg, text, border, label, spin } = cfg;

  return (
    <div
      className={`inline-flex items-center gap-2 rounded-full border ${border} ${bg} ${text} px-3 py-1 text-xs mb-4`}
      role="status"
      aria-live="polite"
      title={
        status === 'verified' ? `Decoded ${detail?.decoded || ''} · ECC-H`
        : status === 'mismatch' ? `Decoded ${detail?.decoded || ''} — expected ${detail?.targetUrl || ''}`
        : status === 'unreadable' ? 'The server-side decoder could not read the current QR'
        : status === 'error' ? 'Could not reach the scan-test endpoint'
        : 'Verifying the QR is machine-readable and resolves to the destination URL'
      }
      data-testid={`wedding-qr-scan-status-${status}`}
    >
      <Icon className={`w-3.5 h-3.5 ${spin ? 'animate-spin' : ''}`} aria-hidden="true" />
      <span>{label}</span>
      {status === 'verified' && detail?.hasMonogram && (
        <span className="text-[10px] uppercase tracking-widest opacity-70 ml-1">with monogram</span>
      )}
    </div>
  );
};
